import { FormikValues } from 'formik';
import React, { ReactElement } from 'react';
import { IModalProps } from '../Modal';
import { IWizardMetaProps } from './utils';
import { IWizardFormStepProps } from './WizardFormStep';
import { IWizardStaticStepProps } from './WizardStaticStep';
import { WizardStep } from './WizardStep';

export interface IWizardStepComponent extends ReactElement<{}> {
  values?: FormikValues;
  asyncSubmitForm?: () => void;
  validate?: () => boolean;
}

export interface IWizardSidebarStepProps {
  index: number;
  id: string;
  isActive: boolean;
  isComplete: boolean;
  label: string;
}
export interface IWizardSidebarProps {
  steps: IWizardSidebarStepProps[];
  onSelect: (index: number) => void;
  sidebarRenderer?: (props: IWizardSidebarStepProps) => React.ReactNode;
}
export interface IWizardStepProps {
  /**
   *
   * must be unique across all siblings
   */
  id: string;
  /**
   *
   * replacement text for the footer "Back" button
   * @default Back
   */
  previousLabel?: string;
  /**
   *
   * replacement text for the footer "Continue" button
   * @default Continue
   */
  nextLabel?: string;
  /**
   *
   * replacement text for the footer "Save as Draft" button
   * @default Save as Draft
   */
  saveLabel?: string;
  /**
   *
   * label text in the sidebar
   * @default id
   */
  sidebarLabel?: string;
  /**
   *
   * if true ignore this step.  Makes it possible to include steps that may be dynamically enabled later.
   * @default false
   */
  ignore?: boolean;
}

// for internal use we inject some private props to WizardStep
export interface IWizardStepPropsPrivate extends IWizardStepProps {
  /**
   * type of child
   */
  type?: 'form' | 'static' | 'custom';
  /**
   * @ignore
   */
  _isActive?: boolean;
  /**
   * @ignore
   */
  _beforeTransition?: (values: FormikValues) => void;
  /**
   * @ignore
   */
  _width?: number;
  /**
   * @ignore
   */
  _meta?: IWizardMetaProps;
  /**
   * @ignore
   */
  _onSave?: () => void;
  /**
   * @ignore
   */
  _onClose?: () => void;
  /**
   * @ignore
   */
  _onComplete?: () => void;
  /**
   * @ignore
   */
  _onNext?: (values: FormikValues) => void;
  /**
   * @ignore
   */
  _onPrevious?: () => void;
  /**
   * @ignore
   */
  _onRef?: (ref: any) => void;
}

export type WizardStepNode = ReactElement<IWizardStepProps>;

export interface IWizardProps {
  /**
   *
   * id of the current Wizard Step.
   */
  currentId: string;
  isOpen: boolean;
  /**
   *
   * title to  display in the modal header section
   */
  title?: string;
  /**
   *
   * called after Wizard successfully validates the current step.
   * when the currentId is the last step then nextId is null
   */
  beforeTransition: (
    values: FormikValues, // validated values for the current step OR an empty object
    currentId: string,
    nextId: string | null // null means we are on the last step
  ) => void;
  onSave?: () => void;
  onClose?: () => void;
  /**
   *
   * an optional renderer for step in the sidebar.
   */
  sidebarRenderer?: (props: IWizardSidebarStepProps) => ReactElement<any>;
  /**
   *
   * WizardStep | WizardStep[]
   */
  children:
    | WizardStep
    | ReactElement<IWizardFormStepProps>
    | ReactElement<IWizardStaticStepProps>
    | (
        | WizardStep
        | ReactElement<IWizardFormStepProps>
        | ReactElement<IWizardStaticStepProps>)[];
  /**
   *
   * Helper attribute for QA. Adds "qa-id" attribute to component's container
   */
  'qa-id'?: string;
  /**
   * what size of modal to use to contain the wizard, defaults to fullscreen
   */
  size?: IModalProps['size'];
}
